import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Shield, CheckCircle2, AlertTriangle, Activity, Heart, Brain, Stethoscope } from 'lucide-react';

/**
 * Health Risk Check — a calm, 60-second assessment that takes the user's
 * age, lifestyle signals, family history, and current symptoms and surfaces
 * a personalised, non-diagnostic risk snapshot.
 *
 * Wired from the Dashboard "Check My Health Risk" CTA.
 */

const STEPS = ['age', 'lifestyle', 'family', 'symptoms', 'result'];

const LIFESTYLE_OPTIONS = [
  { id: 'smokes', label: 'I smoke or vape regularly' },
  { id: 'alcohol_heavy', label: 'I drink alcohol most days' },
  { id: 'sedentary', label: 'I sit most of the day' },
  { id: 'poor_sleep', label: "I sleep less than 6 hours most nights" },
  { id: 'high_stress', label: 'I feel high stress most days' },
  { id: 'fast_food', label: 'I eat fast food / processed food often' },
];

const FAMILY_OPTIONS = [
  { id: 'heart_disease', label: 'Heart disease' },
  { id: 'diabetes', label: 'Diabetes' },
  { id: 'high_bp', label: 'High blood pressure' },
  { id: 'cancer', label: 'Cancer' },
  { id: 'stroke', label: 'Stroke' },
  { id: 'mental_health', label: 'Mental health conditions' },
];

const SYMPTOM_OPTIONS = [
  { id: 'fatigue', label: 'Persistent fatigue' },
  { id: 'chest_disc', label: 'Occasional chest discomfort' },
  { id: 'breath_short', label: 'Shortness of breath on stairs' },
  { id: 'headaches', label: 'Frequent headaches' },
  { id: 'sleep_trouble', label: 'Trouble sleeping' },
  { id: 'mood_low', label: 'Low mood / anxiety' },
  { id: 'weight_change', label: 'Unintentional weight change' },
  { id: 'none', label: 'None of the above' },
];

const RED_FLAGS = ['chest_disc', 'breath_short', 'weight_change'];

const computeRisk = ({ age, lifestyle, family, symptoms }) => {
  const ageNum = parseInt(age, 10) || 0;
  let cardio = 0;
  let metabolic = 0;
  let mental = 0;
  let screening = 0;

  // Age weighting
  if (ageNum >= 60) { cardio += 3; metabolic += 2; screening += 3; }
  else if (ageNum >= 45) { cardio += 2; metabolic += 2; screening += 2; }
  else if (ageNum >= 30) { cardio += 1; metabolic += 1; screening += 1; }

  // Lifestyle
  if (lifestyle.includes('smokes')) { cardio += 3; }
  if (lifestyle.includes('alcohol_heavy')) { cardio += 1; mental += 1; }
  if (lifestyle.includes('sedentary')) { cardio += 2; metabolic += 2; }
  if (lifestyle.includes('poor_sleep')) { mental += 2; cardio += 1; }
  if (lifestyle.includes('high_stress')) { mental += 3; cardio += 1; }
  if (lifestyle.includes('fast_food')) { metabolic += 2; cardio += 1; }

  // Family history
  if (family.includes('heart_disease')) cardio += 2;
  if (family.includes('diabetes')) metabolic += 2;
  if (family.includes('high_bp')) cardio += 1;
  if (family.includes('cancer')) screening += 2;
  if (family.includes('stroke')) cardio += 2;
  if (family.includes('mental_health')) mental += 2;

  // Symptoms
  if (symptoms.includes('fatigue')) { metabolic += 1; mental += 1; }
  if (symptoms.includes('chest_disc')) cardio += 3;
  if (symptoms.includes('breath_short')) cardio += 2;
  if (symptoms.includes('headaches')) mental += 1;
  if (symptoms.includes('sleep_trouble')) mental += 2;
  if (symptoms.includes('mood_low')) mental += 3;
  if (symptoms.includes('weight_change')) { metabolic += 2; screening += 1; }

  const bandFor = (n) => (n >= 6 ? 'high' : n >= 3 ? 'moderate' : 'low');

  const areas = [
    { key: 'cardio', label: 'Heart & Circulation', icon: Heart, score: cardio, band: bandFor(cardio) },
    { key: 'metabolic', label: 'Metabolic Health', icon: Activity, score: metabolic, band: bandFor(metabolic) },
    { key: 'mental', label: 'Mental & Emotional', icon: Brain, score: mental, band: bandFor(mental) },
    { key: 'screening', label: 'Preventive Screenings', icon: Stethoscope, score: screening, band: bandFor(screening) },
  ].sort((a, b) => b.score - a.score);

  const hasRedFlag = symptoms.some((s) => RED_FLAGS.includes(s));
  const top = areas[0];
  const overall = top.band; // overall mirrors the top area

  const recommendations = [];
  if (lifestyle.includes('smokes')) recommendations.push('Even cutting back on smoking lowers your cardiovascular risk within weeks.');
  if (lifestyle.includes('sedentary')) recommendations.push('Aim for 20–30 minutes of light movement on most days — walking counts.');
  if (lifestyle.includes('poor_sleep')) recommendations.push('Protect 7–8 hours of sleep — it shapes mood, hormones, and heart rate.');
  if (lifestyle.includes('high_stress')) recommendations.push('Build a 5-minute calm ritual into your day (breathing, walk, or stretch).');
  if (lifestyle.includes('fast_food')) recommendations.push('Add one whole-food meal a day — vegetables, lean protein, whole grains.');
  if (top.key === 'screening') recommendations.push('Schedule the screenings appropriate for your age this quarter.');
  if (recommendations.length === 0) recommendations.push("You're already doing well — keep your daily check-ins going to maintain this.");

  return { areas, overall, hasRedFlag, top, recommendations };
};

const BAND_STYLE = {
  low: { text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Low' },
  moderate: { text: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Moderate' },
  high: { text: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200', label: 'Elevated' },
};

const HealthRiskCheck = () => {
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [age, setAge] = useState('');
  const [lifestyle, setLifestyle] = useState([]);
  const [family, setFamily] = useState([]);
  const [symptoms, setSymptoms] = useState([]);

  const step = STEPS[stepIdx];
  // Step 1 starts at 1/5 = 20% so the progress bar is never empty.
  const progress = useMemo(() => ((stepIdx + 1) / STEPS.length) * 100, [stepIdx]);

  const toggle = (list, setList, id) => {
    if (id === 'none') {
      setList(list.includes('none') ? [] : ['none']);
      return;
    }
    const cleaned = list.filter((x) => x !== 'none');
    setList(cleaned.includes(id) ? cleaned.filter((x) => x !== id) : [...cleaned, id]);
  };

  const canAdvance = () => {
    if (step === 'age') return /^\d{1,3}$/.test(age) && parseInt(age, 10) >= 12 && parseInt(age, 10) <= 110;
    return true; // lifestyle / family / symptoms allow empty selection
  };

  const next = () => {
    if (!canAdvance()) return;
    setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  };
  const back = () => setStepIdx((i) => Math.max(i - 1, 0));

  const result = useMemo(() => {
    if (step !== 'result') return null;
    return computeRisk({ age, lifestyle, family, symptoms });
  }, [step, age, lifestyle, family, symptoms]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50/40 via-white to-white pb-24" data-testid="health-risk-check">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <button
          onClick={() => (stepIdx === 0 ? navigate('/dashboard') : back())}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4"
          data-testid="health-risk-back"
        >
          <ArrowLeft className="h-4 w-4" />
          {stepIdx === 0 ? 'Back to Home' : 'Back'}
        </button>

        <header className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-red-500 flex items-center justify-center shrink-0">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight" data-testid="health-risk-title">
                Health Risk Check
              </h1>
              <p className="text-xs text-gray-500 mt-0.5">A 60-second snapshot. Not a diagnosis.</p>
            </div>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-4">
            <div
              className="h-full bg-gradient-to-r from-rose-500 to-red-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
              data-testid="health-risk-progress"
            />
          </div>
        </header>

        {step === 'age' && (
          <section className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5" data-testid="health-risk-step-age">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">Step 1 of 4</p>
            <h2 className="text-lg font-bold text-gray-900 mb-1">How old are you?</h2>
            <p className="text-sm text-gray-600 mb-4">We use this to weight age-based risks.</p>
            <input
              type="number"
              inputMode="numeric"
              min="12"
              max="110"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              placeholder="Enter your age"
              className="w-full text-lg font-semibold border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-rose-400"
              data-testid="health-risk-age-input"
            />
          </section>
        )}

        {step === 'lifestyle' && (
          <section className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5" data-testid="health-risk-step-lifestyle">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">Step 2 of 4</p>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Lifestyle signals</h2>
            <p className="text-sm text-gray-600 mb-4">Select what applies to you. Skip if none.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {LIFESTYLE_OPTIONS.map((opt) => {
                const active = lifestyle.includes(opt.id);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => toggle(lifestyle, setLifestyle, opt.id)}
                    className={`text-left text-sm font-medium rounded-xl border-2 px-3 py-3 transition-all ${
                      active ? 'border-rose-500 bg-rose-50 text-rose-900' : 'border-gray-200 hover:border-rose-300 text-gray-700'
                    }`}
                    data-testid={`health-risk-lifestyle-${opt.id}`}
                  >
                    {active && <CheckCircle2 className="h-4 w-4 inline mr-1.5 text-rose-600" />}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {step === 'family' && (
          <section className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5" data-testid="health-risk-step-family">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">Step 3 of 4</p>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Family history</h2>
            <p className="text-sm text-gray-600 mb-4">Conditions that run in your family.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {FAMILY_OPTIONS.map((opt) => {
                const active = family.includes(opt.id);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => toggle(family, setFamily, opt.id)}
                    className={`text-left text-sm font-medium rounded-xl border-2 px-3 py-3 transition-all ${
                      active ? 'border-rose-500 bg-rose-50 text-rose-900' : 'border-gray-200 hover:border-rose-300 text-gray-700'
                    }`}
                    data-testid={`health-risk-family-${opt.id}`}
                  >
                    {active && <CheckCircle2 className="h-4 w-4 inline mr-1.5 text-rose-600" />}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {step === 'symptoms' && (
          <section className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5" data-testid="health-risk-step-symptoms">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">Step 4 of 4</p>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Anything you're feeling?</h2>
            <p className="text-sm text-gray-600 mb-4">Select what you've noticed recently.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {SYMPTOM_OPTIONS.map((opt) => {
                const active = symptoms.includes(opt.id);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => toggle(symptoms, setSymptoms, opt.id)}
                    className={`text-left text-sm font-medium rounded-xl border-2 px-3 py-3 transition-all ${
                      active ? 'border-rose-500 bg-rose-50 text-rose-900' : 'border-gray-200 hover:border-rose-300 text-gray-700'
                    }`}
                    data-testid={`health-risk-symptom-${opt.id}`}
                  >
                    {active && <CheckCircle2 className="h-4 w-4 inline mr-1.5 text-rose-600" />}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {step === 'result' && result && (
          <section data-testid="health-risk-result">
            {result.hasRedFlag && (
              <div className="rounded-2xl border-2 border-rose-200 bg-rose-50 p-4 mb-4 flex gap-3" data-testid="health-risk-redflag">
                <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-rose-900">Talk to a clinician soon</p>
                  <p className="text-sm text-rose-800 mt-1">
                    A symptom you mentioned (chest discomfort, shortness of breath, or unintentional weight change)
                    deserves a proper medical review. This tool is not a diagnosis.
                  </p>
                </div>
              </div>
            )}

            <div
              className={`rounded-2xl border-2 ${BAND_STYLE[result.overall].border} ${BAND_STYLE[result.overall].bg} p-5 mb-4`}
            >
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1">Overall snapshot</p>
              <h2 className="text-2xl font-bold text-gray-900">
                <span className={BAND_STYLE[result.overall].text}>{BAND_STYLE[result.overall].label}</span> overall risk band
              </h2>
              <p className="text-sm text-gray-700 mt-2 leading-relaxed">
                Your strongest signal today is around <span className="font-semibold">{result.top.label}</span>.
                Habits compound — small steady steps move this band over weeks.
              </p>
            </div>

            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-4">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3">Risk areas</p>
              <ul className="space-y-2.5">
                {result.areas.map((area) => {
                  const Ai = area.icon;
                  const style = BAND_STYLE[area.band];
                  return (
                    <li key={area.key} className="flex items-center gap-3" data-testid={`health-risk-area-${area.key}`}>
                      <span className="w-9 h-9 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                        <Ai className="h-4 w-4 text-gray-700" />
                      </span>
                      <span className="text-sm font-medium text-gray-800 flex-1">{area.label}</span>
                      <span
                        className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${style.border} ${style.bg} ${style.text}`}
                      >
                        {style.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-4">
              <p className="text-[11px] font-semibold text-rose-700 uppercase tracking-widest mb-3">Where to start</p>
              <ul className="space-y-2.5">
                {result.recommendations.map((r) => (
                  <li key={r} className="flex items-start gap-2.5">
                    <CheckCircle2 className="h-4 w-4 text-rose-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-700 leading-relaxed">{r}</p>
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-[11px] text-gray-400 text-center mb-4">
              HealthLoop supports awareness — it does not diagnose or replace medical care.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => navigate('/preventive-health')}
                className="bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-xl px-5 py-3 shadow-sm transition-all"
                data-testid="health-risk-cta-track-metrics"
              >
                Track my metrics →
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-white hover:bg-gray-50 text-gray-900 font-semibold rounded-xl px-5 py-3 border border-gray-200 transition-all"
                data-testid="health-risk-cta-back-dashboard"
              >
                Back to dashboard
              </button>
            </div>
          </section>
        )}

        {step !== 'result' && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={next}
              disabled={!canAdvance()}
              className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 font-semibold shadow-sm transition-all ${
                canAdvance()
                  ? 'bg-gradient-to-br from-rose-500 to-red-500 hover:from-rose-600 hover:to-red-600 text-white'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
              data-testid="health-risk-next"
            >
              {stepIdx === STEPS.length - 2 ? 'Get my snapshot' : 'Continue'}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HealthRiskCheck;
